import React, { useState, useEffect, useCallback } from 'react';
import apiKeyService from '../../services/apiKeyService';
import UsageChart from './UsageChart';

// 프로바이더 정보는 백엔드에서 로드

const ApiKeyManager = () => {
  const [keys, setKeys] = useState([]);
  const [providers, setProviders] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(null); // keyId for project assignment
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [message, setMessage] = useState(null);
  const [balanceInfo, setBalanceInfo] = useState({});
  const [checkingBalanceId, setCheckingBalanceId] = useState(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelValue, setEditingLabelValue] = useState('');

  // 새 키 추가 폼 상태
  const [newKey, setNewKey] = useState({
    provider: '',
    label: '',
    apiKey: '',
    model: ''
  });

  // 데이터 로드
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [keysRes, statsRes, providersRes] = await Promise.all([
        apiKeyService.getApiKeys(),
        apiKeyService.getApiKeyStats(),
        apiKeyService.getProviders()
      ]);

      if (keysRes.success) {
        setKeys(keysRes.keys);
        // API가 projects도 함께 반환
        if (keysRes.projects) {
          setProjects(keysRes.projects);
        }
      }
      if (statsRes.success) {
        setStats(statsRes.stats);
      }
      if (providersRes.success) {
        // 배열을 객체로 변환
        const providersObj = {};
        providersRes.providers.forEach(p => {
          providersObj[p.id] = p;
        });
        setProviders(providersObj);
      }
      setError(null);
    } catch (err) {
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 메시지 자동 숨김
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // 키 추가
  const handleAddKey = async (e) => {
    e.preventDefault();
    try {
      const result = await apiKeyService.addApiKey(
        newKey.provider,
        newKey.label,
        newKey.apiKey,
        newKey.model || providers[newKey.provider]?.defaultModel
      );

      if (result.success) {
        setShowAddForm(false);
        setNewKey({ provider: '', label: '', apiKey: '', model: '' });
        setMessage({ type: 'success', text: 'API 키가 추가되었습니다.' });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '키 추가에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '키 추가 중 오류가 발생했습니다.' });
    }
  };

  // 프로바이더 변경 시 기본 모델 설정
  const handleProviderChange = (provider) => {
    setNewKey({
      ...newKey,
      provider,
      model: providers[provider]?.defaultModel || ''
    });
  };

  // 이름 편집 시작
  const handleLabelEditStart = (keyId, currentLabel) => {
    setEditingLabelId(keyId);
    setEditingLabelValue(currentLabel);
  };

  // 이름 편집 저장
  const handleLabelEditSave = async (keyId) => {
    if (!editingLabelValue.trim()) {
      setMessage({ type: 'error', text: '이름을 입력해주세요.' });
      return;
    }
    try {
      const result = await apiKeyService.updateApiKey(keyId, { label: editingLabelValue.trim() });
      if (result.success) {
        setMessage({ type: 'success', text: '이름이 변경되었습니다.' });
        setEditingLabelId(null);
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '이름 변경에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '이름 변경 중 오류가 발생했습니다.' });
    }
  };

  // 이름 편집 취소
  const handleLabelEditCancel = () => {
    setEditingLabelId(null);
    setEditingLabelValue('');
  };

  // 모델 변경
  const handleModelChange = async (keyId, newModel) => {
    try {
      const result = await apiKeyService.updateApiKey(keyId, { model: newModel });
      if (result.success) {
        setMessage({ type: 'success', text: '모델이 변경되었습니다.' });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '모델 변경에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '모델 변경 중 오류가 발생했습니다.' });
    }
  };

  // 키 활성화/비활성화
  const handleToggleActive = async (id, currentActive) => {
    try {
      const result = await apiKeyService.updateApiKey(id, { isActive: !currentActive });
      if (result.success) {
        loadData();
      }
    } catch (err) {
      setMessage({ type: 'error', text: '상태 변경에 실패했습니다.' });
    }
  };

  // 키 삭제
  const handleDelete = async (id, label) => {
    if (!window.confirm(`"${label}" 키를 삭제하시겠습니까?`)) return;

    try {
      const result = await apiKeyService.deleteApiKey(id);
      if (result.success) {
        setMessage({ type: 'success', text: '키가 삭제되었습니다.' });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '삭제에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '삭제 중 오류가 발생했습니다.' });
    }
  };

  // 키 테스트
  const handleTest = async (id) => {
    setTestingId(id);
    setTestResult(null);

    try {
      const result = await apiKeyService.testApiKey(id);
      if (result.success) {
        setTestResult({ id, ...result.test });
      }
    } catch (err) {
      setTestResult({ id, success: false, message: '테스트 중 오류 발생' });
    } finally {
      setTestingId(null);
    }
  };

  // 잔액 확인
  const handleCheckBalance = async (id) => {
    setCheckingBalanceId(id);
    try {
      const result = await apiKeyService.getApiKeyBalance(id);
      if (result.success) {
        setBalanceInfo(prev => ({ ...prev, [id]: result.balance }));
      }
    } catch (err) {
      setBalanceInfo(prev => ({ ...prev, [id]: { supported: false, message: '조회 실패' } }));
    } finally {
      setCheckingBalanceId(null);
    }
  };

  // 프로젝트 할당
  const handleAssignProject = async (keyId, projectId) => {
    try {
      const result = await apiKeyService.assignProject(keyId, projectId, { isPrimary: false });
      if (result.success) {
        setMessage({ type: 'success', text: '프로젝트가 할당되었습니다.' });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '프로젝트 할당에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '프로젝트 할당 중 오류가 발생했습니다.' });
    }
  };

  // 프로젝트 할당 해제
  const handleUnassignProject = async (keyId, projectId) => {
    try {
      const result = await apiKeyService.unassignProject(keyId, projectId);
      if (result.success) {
        setMessage({ type: 'success', text: '프로젝트 할당이 해제되었습니다.' });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '프로젝트 할당 해제에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '프로젝트 할당 해제 중 오류가 발생했습니다.' });
    }
  };

  // 필터링된 키 목록
  const filteredKeys = selectedProject === 'all'
    ? keys
    : keys.filter(key => key.projects?.some(p => p.code === selectedProject));

  if (loading) {
    return <div className="p-0 flex items-center justify-center min-h-[200px] text-gray-600">로딩 중...</div>;
  }

  return (
    <div className="p-0">
      {/* 헤더 */}
      <div className="flex justify-between items-start mb-8 gap-4 max-md:flex-col max-md:items-stretch">
        <div>
          <h3 className="text-2xl font-bold m-0 mb-2 text-[#1a1a1a]">AI API 키 관리</h3>
          <p className="text-[15px] text-gray-600 m-0 leading-relaxed">AI 서비스 연동에 필요한 API 키를 관리합니다.</p>
        </div>
        <div className="flex items-center gap-2 max-md:flex-col max-md:items-start">
          <label className="text-[15px] text-gray-600">프로젝트:</label>
          <select
            value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg text-[15px] bg-white min-w-[150px] max-md:w-full"
          >
            <option value="all">전체 ({keys.length})</option>
            {projects.map(p => (
              <option key={p.code} value={p.code}>
                {p.name} ({keys.filter(k => k.projects?.some(kp => kp.code === p.code)).length})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 메시지 */}
      {message && (
        <div className={`px-4 py-4 rounded-lg mb-6 text-[15px] leading-relaxed ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-600 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {error && <div className="px-4 py-4 bg-red-50 border border-red-200 rounded-lg text-red-600 mb-6 text-[15px]">{error}</div>}

      {/* 사용량 그래프 */}
      <UsageChart />

      {/* 프로바이더별 키 상태 요약 */}
      <div className="grid grid-cols-4 gap-4 mb-12 max-lg:grid-cols-2 max-sm:grid-cols-1">
        {Object.entries(providers).map(([key, info]) => {
          const providerKeys = keys.filter(k => k.provider === key);
          const activeKeys = providerKeys.filter(k => k.isActive);
          const hasKey = providerKeys.length > 0;

          return (
            <div key={key} className={`bg-white border-2 rounded-xl px-4 py-6 flex flex-col gap-4 transition-all hover:-translate-y-0.5 hover:shadow-lg ${hasKey ? 'border-green-500 bg-gradient-to-b from-green-50 to-white' : 'border-gray-200 bg-slate-50'}`}>
              <div className="flex justify-between items-center">
                <span className="font-bold text-[15px] text-[#1a1a1a]">{info.name}</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wide ${info.tier === 'free' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                  {info.tier === 'free' ? '무료' : '유료'}
                </span>
              </div>
              <div className="text-center py-4">
                {hasKey ? (
                  <>
                    <span className="text-[32px] font-bold text-green-500 leading-none">{activeKeys.length}/{providerKeys.length}</span>
                    <span className="block text-xs text-gray-600 mt-2">활성 키</span>
                  </>
                ) : (
                  <span className="text-[15px] text-gray-400">키 없음</span>
                )}
              </div>
              <a
                href={info.registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center px-4 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs font-semibold text-blue-500 no-underline transition-all hover:bg-blue-50 hover:border-blue-500 hover:-translate-y-0.5"
              >
                {hasKey ? '키 발급 페이지' : '키 발급받기'} →
              </a>
            </div>
          );
        })}
      </div>

      {/* 새 키 추가 버튼 */}
      <div className="my-8 text-right">
        <button
          className="px-6 py-3 min-h-[44px] bg-blue-500 text-white border-none rounded-lg text-[15px] font-semibold cursor-pointer whitespace-nowrap transition-all inline-flex items-center justify-center hover:bg-blue-600 hover:-translate-y-0.5 hover:shadow-lg max-md:w-full"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? '취소' : '+ 새 키 추가'}
        </button>
      </div>

      {/* 새 키 추가 폼 */}
      {showAddForm && (
        <form className="bg-white border-2 border-slate-200 rounded-xl p-6 mb-12 shadow-sm" onSubmit={handleAddKey}>
          <h4 className="text-2xl font-bold m-0 mb-6 text-[#1a1a1a]">새 API 키 추가</h4>
          <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
            <div className="mb-6">
              <label className="block text-[15px] font-semibold mb-2 text-[#1a1a1a]">프로바이더</label>
              <select
                value={newKey.provider}
                onChange={e => handleProviderChange(e.target.value)}
                required
                className="w-full min-h-[44px] px-4 py-2 border-2 border-slate-200 rounded-lg text-[15px] transition-all bg-white box-border hover:border-slate-300 focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
              >
                <option value="">선택하세요</option>
                {Object.entries(providers).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.name} ({info.tier === 'free' ? '무료' : '유료'})
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-6">
              <label className="block text-[15px] font-semibold mb-2 text-[#1a1a1a]">라벨 (구분용)</label>
              <input
                type="text"
                value={newKey.label}
                onChange={e => setNewKey({ ...newKey, label: e.target.value })}
                placeholder="예: 메인 키, 백업 키"
                required
                className="w-full min-h-[44px] px-4 py-2 border-2 border-slate-200 rounded-lg text-[15px] transition-all bg-white box-border hover:border-slate-300 focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
              />
            </div>
          </div>
          {newKey.provider && providers[newKey.provider]?.models && (
            <div className="mb-6">
              <label className="block text-[15px] font-semibold mb-2 text-[#1a1a1a]">사용 모델</label>
              <select
                value={newKey.model}
                onChange={e => setNewKey({ ...newKey, model: e.target.value })}
                required
                className="w-full min-h-[44px] px-4 py-2 border-2 border-slate-200 rounded-lg text-[15px] transition-all bg-white box-border hover:border-slate-300 focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
              >
                {providers[newKey.provider].models.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name} - {m.description}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="mb-6">
            <label className="block text-[15px] font-semibold mb-2 text-[#1a1a1a]">API 키</label>
            <input
              type="password"
              value={newKey.apiKey}
              onChange={e => setNewKey({ ...newKey, apiKey: e.target.value })}
              placeholder="API 키 입력..."
              required
              className="w-full min-h-[44px] px-4 py-2 border-2 border-slate-200 rounded-lg text-[15px] transition-all bg-white box-border hover:border-slate-300 focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
            />
          </div>
          {newKey.provider && providers[newKey.provider] && (
            <div className="bg-sky-50 border-2 border-sky-200 rounded-lg p-4 mb-6">
              <p className="text-[15px] text-sky-700 m-0 mb-2 leading-relaxed">{providers[newKey.provider].description}</p>
              <a
                href={providers[newKey.provider].registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[15px] text-sky-600 font-semibold underline hover:text-sky-700"
              >
                {providers[newKey.provider].name} 콘솔에서 키 발급받기 →
              </a>
            </div>
          )}
          <button type="submit" className="min-h-[44px] px-6 py-3 bg-blue-500 text-white border-none rounded-lg text-[15px] font-semibold cursor-pointer transition-all inline-flex items-center justify-center hover:bg-blue-600 hover:-translate-y-0.5 hover:shadow-lg disabled:bg-slate-300 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none">추가하기</button>
        </form>
      )}

      {/* 등록된 키 목록 */}
      <div className="mb-12">
        <h4 className="text-2xl font-bold m-0 mb-6 text-[#1a1a1a]">등록된 API 키 ({filteredKeys.length}{selectedProject !== 'all' ? ` / ${keys.length}` : ''})</h4>
        {filteredKeys.length === 0 ? (
          <div className="text-center py-12 px-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl">
            <p className="m-0 text-gray-600 font-semibold text-[15px] mb-2">등록된 API 키가 없습니다.</p>
            <p className="m-0 text-gray-600 text-[15px]">위의 "새 키 추가" 버튼을 눌러 키를 등록하세요.</p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4 max-sm:grid-cols-1">
            {filteredKeys.map(key => (
              <div key={key.id} className={`relative bg-white border-2 border-slate-200 rounded-xl p-6 transition-all hover:shadow-lg hover:-translate-y-0.5 ${!key.isActive ? 'opacity-50 bg-slate-50' : ''}`}>
                <div className="flex justify-between items-center mb-4">
                  <span className={`inline-block px-4 py-1.5 rounded-md text-xs font-bold tracking-wide ${providers[key.provider]?.tier === 'free' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                    {key.providerInfo?.name || key.provider}
                  </span>
                  <label className="relative inline-block w-10 h-[22px]">
                    <input
                      type="checkbox"
                      checked={key.isActive}
                      onChange={() => handleToggleActive(key.id, key.isActive)}
                      className="opacity-0 w-0 h-0"
                    />
                    <span className={`absolute cursor-pointer inset-0 rounded-[22px] transition-all duration-300 ${key.isActive ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <span className={`absolute h-4 w-4 left-[3px] bottom-[3px] bg-white rounded-full transition-all duration-300 ${key.isActive ? 'translate-x-[18px]' : ''}`}></span>
                    </span>
                  </label>
                </div>
                <div className="mb-4">
                  <div className="mb-2">
                    {editingLabelId === key.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingLabelValue}
                          onChange={e => setEditingLabelValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleLabelEditSave(key.id);
                            if (e.key === 'Escape') handleLabelEditCancel();
                          }}
                          autoFocus
                          className="flex-1 px-4 py-2 text-[15px] border-2 border-blue-500 rounded-lg outline-none"
                        />
                        <button className="px-4 py-2 text-xs font-semibold border-none rounded-md cursor-pointer transition-all bg-blue-500 text-white hover:bg-blue-600" onClick={() => handleLabelEditSave(key.id)}>✓</button>
                        <button className="px-4 py-2 text-xs font-semibold border-none rounded-md cursor-pointer transition-all bg-slate-100 text-gray-600 hover:bg-slate-200" onClick={handleLabelEditCancel}>✕</button>
                      </div>
                    ) : (
                      <div className="text-[15px] font-semibold text-[#1a1a1a] flex items-center gap-2 cursor-pointer transition-colors hover:text-blue-500 group" onClick={() => handleLabelEditStart(key.id, key.label)}>
                        {key.label}
                        <span className="text-xs opacity-0 group-hover:opacity-70 transition-opacity">✎</span>
                      </div>
                    )}
                  </div>
                  <div className="font-mono text-xs text-gray-600 mb-2 p-2 bg-slate-50 rounded">{key.maskedKey}</div>
                  <div className="flex flex-wrap items-center gap-2 my-4 p-2 bg-slate-50 rounded-lg">
                    <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">모델:</span>
                    <select
                      className="flex-1 px-2 py-1.5 border-2 border-slate-200 rounded-md text-xs bg-white text-[#1a1a1a] cursor-pointer transition-all hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
                      value={key.model || providers[key.provider]?.defaultModel || ''}
                      onChange={e => handleModelChange(key.id, e.target.value)}
                      title={providers[key.provider]?.models?.find(m => m.id === key.model)?.description || ''}
                    >
                      {providers[key.provider]?.models?.map(m => (
                        <option key={m.id} value={m.id} title={m.description}>{m.name}</option>
                      ))}
                    </select>
                    <span className="w-full text-xs text-gray-500 mt-1 leading-relaxed">
                      {providers[key.provider]?.models?.find(m => m.id === key.model)?.description || ''}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">사용: {key.usageCount.toLocaleString()}회</div>
                  {/* 프로젝트 태그 */}
                  <div className="flex items-center flex-wrap gap-1 mt-2 pt-2 border-t border-slate-200">
                    <span className="text-xs text-gray-600 mr-2">프로젝트:</span>
                    <div className="flex flex-wrap gap-1 items-center">
                      {key.projects && key.projects.length > 0 ? (
                        key.projects.map(p => (
                          <span key={p.code} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${p.isPrimary ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-sky-50 text-sky-700'}`}>
                            {p.name}
                            <button
                              className="bg-transparent border-none text-inherit cursor-pointer px-0.5 text-sm leading-none opacity-60 hover:opacity-100"
                              onClick={() => handleUnassignProject(key.id, p.id)}
                              title="할당 해제"
                            >×</button>
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 italic">미할당</span>
                      )}
                      <button
                        className="w-[22px] h-[22px] rounded border border-dashed border-slate-200 bg-transparent text-gray-600 cursor-pointer text-sm flex items-center justify-center hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50"
                        onClick={() => setShowProjectModal(key.id)}
                        title="프로젝트 추가"
                      >+</button>
                    </div>
                  </div>
                </div>
                {/* 프로젝트 할당 모달 */}
                {showProjectModal === key.id && (
                  <div className="absolute top-full right-0 z-[100] bg-white border border-slate-200 rounded-lg shadow-lg min-w-[200px] mt-1">
                    <div className="flex justify-between items-center px-4 py-2 border-b border-slate-200 font-semibold text-[15px]">
                      <span>프로젝트 할당</span>
                      <button className="bg-transparent border-none text-lg cursor-pointer text-gray-600" onClick={() => setShowProjectModal(null)}>×</button>
                    </div>
                    <div className="p-2 max-h-[200px] overflow-y-auto">
                      {projects
                        .filter(p => !key.projects?.some(kp => kp.code === p.code))
                        .map(p => (
                          <button
                            key={p.code}
                            className="flex flex-col w-full px-4 py-2 border-none bg-transparent text-left cursor-pointer rounded-md hover:bg-slate-100"
                            onClick={() => {
                              handleAssignProject(key.id, p.id);
                              setShowProjectModal(null);
                            }}
                          >
                            {p.name}
                            <span className="text-xs text-gray-400">{p.subdomain}</span>
                          </button>
                        ))}
                      {projects.filter(p => !key.projects?.some(kp => kp.code === p.code)).length === 0 && (
                        <p className="text-center text-gray-600 text-xs p-4">모든 프로젝트에 할당됨</p>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 mt-4 max-md:flex-col">
                  <button
                    className="flex-1 px-4 py-2 border-2 border-transparent rounded-lg text-xs font-semibold cursor-pointer transition-all inline-flex items-center justify-center min-h-[36px] bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-200 hover:border-sky-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none max-md:w-full"
                    onClick={() => handleTest(key.id)}
                    disabled={testingId === key.id}
                  >
                    {testingId === key.id ? '테스트 중...' : '테스트'}
                  </button>
                  <button
                    className="flex-1 px-4 py-2 border-2 border-transparent rounded-lg text-xs font-semibold cursor-pointer transition-all inline-flex items-center justify-center min-h-[36px] bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200 hover:border-amber-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none max-md:w-full"
                    onClick={() => handleCheckBalance(key.id)}
                    disabled={checkingBalanceId === key.id}
                  >
                    {checkingBalanceId === key.id ? '조회 중...' : '잔액'}
                  </button>
                  <button
                    className="flex-1 px-4 py-2 border-2 border-transparent rounded-lg text-xs font-semibold cursor-pointer transition-all inline-flex items-center justify-center min-h-[36px] bg-red-100 text-red-600 border-red-200 hover:bg-red-200 hover:border-red-300 hover:-translate-y-0.5 max-md:w-full"
                    onClick={() => handleDelete(key.id, key.label)}
                  >
                    삭제
                  </button>
                </div>
                {testResult && testResult.id === key.id && (
                  <div className={`mt-4 p-4 rounded-lg text-[15px] leading-relaxed border-2 ${testResult.success ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-600 border-red-200'}`}>
                    {testResult.success ? '✓' : '✗'} {testResult.message}
                  </div>
                )}
                {balanceInfo[key.id] && (
                  <div className={`mt-4 p-4 rounded-lg text-[15px] leading-relaxed border-2 ${balanceInfo[key.id].supported ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    {balanceInfo[key.id].supported ? (
                      <>
                        <div className="flex items-center gap-2 mb-2 text-[15px] font-bold text-green-800">
                          <span className="font-semibold text-gray-600 mr-1">플랜:</span> {balanceInfo[key.id].plan}
                        </div>
                        {balanceInfo[key.id].usageThisMonth && (
                          <div className="flex items-center gap-2 mb-2 text-[15px] font-semibold text-sky-700">
                            <span className="font-semibold text-gray-600 mr-1">이번달 사용:</span> ${balanceInfo[key.id].usageThisMonth}
                          </div>
                        )}
                        {balanceInfo[key.id].limit && (
                          <div className="flex items-center gap-2 mb-2 text-[15px] text-gray-600">
                            <span className="font-semibold text-gray-600 mr-1">한도:</span> {balanceInfo[key.id].limit}
                          </div>
                        )}
                        {balanceInfo[key.id].message && (
                          <div className="text-amber-800 text-xs mt-2 p-2 bg-amber-100/50 rounded">{balanceInfo[key.id].message}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-amber-800 text-xs p-2 bg-amber-100/50 rounded">{balanceInfo[key.id].message}</div>
                        {balanceInfo[key.id].dashboardUrl && (
                          <a
                            href={balanceInfo[key.id].dashboardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block mt-4 px-4 py-2 text-xs font-semibold text-white bg-blue-500 rounded-md no-underline transition-all hover:bg-blue-600 hover:-translate-y-0.5 hover:shadow-md"
                          >
                            대시보드에서 확인 →
                          </a>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 오늘 사용량 & 최근 에러 */}
      {stats && (
        <div className="bg-white border-2 border-slate-200 rounded-xl p-6 mt-12 shadow-sm">
          <div className="text-[15px] text-gray-600 leading-relaxed">
            오늘 API 호출: <strong className="inline-block text-blue-500 text-2xl font-bold mx-1">{stats.todayUsage}</strong>회
          </div>
          {stats.recentErrors?.length > 0 && (
            <div className="mt-6 pt-6 border-t-2 border-slate-200">
              <h4 className="text-[15px] font-bold text-red-600 m-0 mb-4">최근 오류 ({stats.recentErrors.length})</h4>
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {stats.recentErrors.slice(0, 3).map((err, idx) => (
                  <li key={idx} className="flex gap-4 text-[15px] p-4 bg-red-50 border border-red-100 rounded-lg items-start max-md:flex-col max-md:gap-2">
                    <span className="font-bold text-[#1a1a1a] min-w-[80px] flex-shrink-0 max-md:min-w-0">{err.provider}</span>
                    <span className="text-red-600 flex-1 leading-relaxed">{err.errorMessage}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApiKeyManager;
